import { useEffect, useState, type ReactNode } from "react"
import { Trans, useLingui } from "@lingui/react/macro"
import type { CallSnapshot } from "@/types/v2/voice"
import { initials } from "@/lib/initials"
import { useMicLevel, MIC_LEVEL_BARS } from "@/hooks/use-mic-level"
import { CallDuration } from "./call-duration"
import { DtmfPad } from "./dtmf-pad"
import {
  SparkleIcon,
  MicLineIcon,
  MicLineOffIcon,
  DotGridIcon,
  NoteDocIcon,
  PhoneDownSolidIcon,
  InfoCircleIcon,
  RefreshIcon,
} from "./icons"

interface ActiveCallWidgetProps {
  call: CallSnapshot
  // The in-call "Quick note" jot, lifted to the softphone so
  // it survives the widget unmounting when the call ends and
  // can seed the post-call card (CON-155 → CON-158).
  noteDraft: string
  onNoteDraftChange: (value: string) => void
  onToggleMute: () => void
  // Hold is removed from the surface but kept wired under the
  // hood (CON-155) so a future affordance can resume it.
  onToggleHold: () => void
  onHangup: () => void
  onSendTone: (tone: string) => void
  onDismiss: () => void
  // Re-place the failed call from the "Couldn't connect" state.
  // Absent when there's nothing to redial (no contact).
  onTryAgain?: () => void
}

// The connected-call control surface, dark (CON-155): header
// (avatar, name, status dot, tabular timer), the "AI is
// listening" pill with a waveform, an optional swap slot (the
// Quick-note jot OR the DTMF keypad), and the control row
// Mute · Keypad · Note · End. A pre-answer "couldn't connect"
// failure renders its own calm error state with Try again /
// Dismiss instead.
export function ActiveCallWidget({
  call,
  noteDraft,
  onNoteDraftChange,
  onToggleMute,
  onHangup,
  onSendTone,
  onDismiss,
  onTryAgain,
}: ActiveCallWidgetProps) {
  // The middle "swap" slot is single-occupancy: the note jot
  // and the keypad are mutually exclusive, so opening one
  // closes the other.
  const [swap, setSwap] = useState<"none" | "note" | "keypad">("none")

  // The SDK client overwrites `state.call` in place when a new
  // call attaches, so this component can survive a call
  // transition without unmounting. Reset the swap slot on a new
  // call id so the next call never opens with a stale Keypad /
  // Note panel.
  useEffect(() => {
    setSwap("none")
  }, [call.sdkCallId])

  // Whether the SERVER says the customer has actually
  // connected. The SDK agent-leg connectedAt is stamped on
  // auto-answer BEFORE the customer bridges, so for an
  // outgoing call we trust the backend VoiceCall's
  // `answeredAt`, not the SDK timer.
  const customerAnswered =
    call.direction === "outgoing"
      ? call.voiceCall?.answeredAt != null
      : call.connectedAt != null

  // An outgoing leg where the customer hasn't answered yet
  // (the agent clicked Call; we're ringing the customer)
  // reads as "Calling…" rather than a running timer.
  const isDialing =
    call.direction === "outgoing" &&
    !customerAnswered &&
    (call.phase === "connecting" || call.phase === "active")

  const failedToConnect = call.endReason === "failed"

  // The remote audio leg dropped mid-call (RemoteMediaRemoved)
  // or the SDK Call is reconnecting. Only meaningful once the
  // customer is on the line and the call isn't terminal.
  const mediaDropped =
    call.mediaState === "dropped" &&
    customerAnswered &&
    !failedToConnect &&
    (call.phase === "active" || call.phase === "connecting")

  const name =
    call.voiceCall?.contact?.name ||
    call.remoteDisplayName ||
    (isDialing ? "your contact" : "On a call")

  // A pre-answer failure is its own calm terminal surface
  // (frame 12) — no pill, no controls.
  if (failedToConnect) {
    return (
      <div className="flex flex-col gap-4 px-[18px] pb-4 pt-[18px]">
        <Header name={name} status="failed" />

        <div className="flex items-center gap-2.5 rounded-[11px] bg-[#2A2620] px-3 py-[11px]">
          <InfoCircleIcon
            width={15}
            height={15}
            className="shrink-0 text-[#E5736F]"
          />
          <span className="text-[12px] leading-4 text-[#D9D3C8]">
            <Trans>The number couldn't be reached. You weren't charged.</Trans>
          </span>
        </div>

        <div className="flex items-center gap-2.5">
          {onTryAgain ? (
            <button
              type="button"
              onClick={onTryAgain}
              className="flex flex-1 items-center justify-center gap-1.5 rounded-[11px] bg-[#F4EFE6] px-3.5 py-2.5 text-[13px] font-semibold leading-4 text-[#1E1B17] transition-colors hover:bg-[#FBF7F0] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E0A56A]/60"
            >
              <RefreshIcon width={14} height={14} />
              <Trans>Try again</Trans>
            </button>
          ) : null}
          <button
            type="button"
            onClick={onDismiss}
            className="rounded-[11px] border border-[#3A352E] px-3.5 py-2.5 text-[13px] font-medium leading-4 text-[#D9D3C8] transition-colors hover:bg-[#2A2620] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E0A56A]/60"
          >
            <Trans>Dismiss</Trans>
          </button>
        </div>
      </div>
    )
  }

  const status: HeaderStatus =
    call.phase === "ended"
      ? "ended"
      : mediaDropped
        ? "reconnecting"
        : call.isMuted && customerAnswered
          ? "muted"
          : isDialing
            ? "dialing"
            : customerAnswered
              ? "connected"
              : "connecting"

  const showTimer = customerAnswered && !isDialing && call.phase !== "ended"

  const onSurface = call.phase === "active" || call.phase === "connecting"

  return (
    <div className="flex flex-col gap-4 px-[18px] pb-4 pt-[18px]">
      <Header
        name={name}
        status={status}
        timer={
          showTimer ? (
            <CallDuration connectedAt={call.connectedAt} />
          ) : undefined
        }
      />

      {/* The AI-listening cue only reads true once the customer
          is on the line and the call is being transcribed. The
          waveform tracks the agent's live mic (flat when
          muted, static fallback without a mic). */}
      {customerAnswered && onSurface ? (
        <ListeningPill muted={call.isMuted} />
      ) : null}

      {onSurface ? (
        <>
          {swap === "keypad" ? <DtmfPad onTone={onSendTone} /> : null}
          {swap === "note" ? (
            <NotePanel
              noteDraft={noteDraft}
              onNoteDraftChange={onNoteDraftChange}
            />
          ) : null}

          <div className="flex items-center gap-2.5 pt-0.5">
            <MuteButton
              isMuted={call.isMuted}
              onToggleMute={onToggleMute}
            />

            <KeypadButton
              swap={swap}
              onToggle={() =>
                setSwap((s) => (s === "keypad" ? "none" : "keypad"))
              }
            />

            <NoteButton
              swap={swap}
              onToggle={() => setSwap((s) => (s === "note" ? "none" : "note"))}
            />

            <HangupButton onHangup={onHangup} />
          </div>
        </>
      ) : null}
    </div>
  )
}

type HeaderStatus =
  | "connected"
  | "muted"
  | "dialing"
  | "connecting"
  | "reconnecting"
  | "ended"
  | "failed"

const STATUS_META: Record<
  HeaderStatus,
  { dot: string; text: string; pulse?: boolean }
> = {
  connected: { dot: "#1FAA53", text: "#A7E8C0" },
  muted: { dot: "#E5484D", text: "#F0B3B5" },
  dialing: { dot: "#C9A24A", text: "#E0C68A" },
  connecting: { dot: "#C9A24A", text: "#E0C68A" },
  reconnecting: {
    dot: "#C9A24A",
    text: "#E0C68A",
    pulse: true,
  },
  ended: { dot: "#6E685F", text: "#9A938A" },
  failed: { dot: "#E5484D", text: "#F0B3B5" },
}

function useStatusLabel(status: HeaderStatus): string {
  const { t } = useLingui()
  const labels: Record<HeaderStatus, string> = {
    connected: t`Connected`,
    muted: t`Muted`,
    dialing: t`Calling…`,
    connecting: t`Connecting…`,
    reconnecting: t`Reconnecting…`,
    ended: t`Call ended`,
    failed: t`Couldn't connect`,
  }
  return labels[status]
}

function Header({
  name,
  status,
  timer,
}: {
  name: string
  status: HeaderStatus
  timer?: ReactNode
}) {
  const meta = STATUS_META[status]
  const label = useStatusLabel(status)
  return (
    <div className="flex items-center gap-3">
      <div className="flex size-[42px] shrink-0 items-center justify-center rounded-full bg-[#3A352E] text-[14px] font-semibold leading-4 text-[#F0EDE6]">
        {initials(name)}
      </div>
      <div className="flex min-w-0 flex-grow flex-col gap-[3px]">
        <span className="truncate text-[15px] font-semibold leading-[18px] text-[#FBF7F0]">
          {name}
        </span>
        <div className="flex items-center gap-1.5">
          <span
            aria-hidden="true"
            className={`size-[7px] shrink-0 rounded-full ${meta.pulse ? "animate-pulse" : ""}`}
            style={{ backgroundColor: meta.dot }}
          />
          <span
            className="text-[12px] font-medium leading-[14px]"
            style={{ color: meta.text }}
          >
            {label}
          </span>
        </div>
      </div>
      {timer ? (
        <span className="shrink-0 text-[15px] font-semibold leading-[18px] text-[#FBF7F0] tabular-nums">
          {timer}
        </span>
      ) : null}
    </div>
  )
}

// Static seven-bar waveform matching the design. The cue is
// the always-on "AI is listening" signal; we keep it steady
// rather than animated so it reads as a state, not noise.
// Static fallback waveform, used when there's no live mic
// level (no AudioContext / mic, or pre-connect). Heights in px
// within the 16px-tall track; `on` bars get the green tint.
const WAVE_BARS: ReadonlyArray<{ h: number; on: boolean }> = [
  { h: 6, on: false },
  { h: 12, on: true },
  { h: 16, on: true },
  { h: 9, on: false },
  { h: 14, on: true },
  { h: 7, on: false },
  { h: 11, on: true },
]

// Bar geometry for the live/muted waveform: a level of 0..1
// maps to MIN..MAX px; bars past ON_THRESHOLD get the green
// tint, quieter ones stay muted.
const BAR_MIN_H = 3
const BAR_MAX_H = 16
const BAR_ON_THRESHOLD = 0.3

function ListeningPill({ muted }: { muted: boolean }) {
  // The mic-level hook lives here (a leaf) so its ~20fps
  // updates only re-render the pill, not the whole call
  // widget. Gated on `!muted` so muting actually releases the
  // mic stream (the browser's in-use indicator turns off),
  // not just flattens the bars.
  const levels = useMicLevel(!muted)

  // Muted → flat resting bars; live levels → mic-reactive
  // heights; otherwise the static fallback pattern.
  const bars: ReadonlyArray<{ h: number; on: boolean }> = muted
    ? Array.from({ length: MIC_LEVEL_BARS }, () => ({
        h: BAR_MIN_H,
        on: false,
      }))
    : levels
      ? levels.map((level) => ({
          h: BAR_MIN_H + level * (BAR_MAX_H - BAR_MIN_H),
          on: level > BAR_ON_THRESHOLD,
        }))
      : WAVE_BARS

  return (
    <div className="flex items-center gap-2.5 rounded-[11px] bg-[#2A2620] px-3 py-[9px]">
      <SparkleIcon width={13} height={13} className="shrink-0 text-[#E0A56A]" />
      <span className="flex-grow text-[12px] font-medium leading-[15px] text-[#E0A56A]">
        <Trans>AI is listening · live transcript</Trans>
      </span>
      <span
        aria-hidden="true"
        className="flex h-4 shrink-0 items-center gap-0.5"
      >
        {bars.map((bar, i) => (
          <span
            key={i}
            className="w-0.5 shrink-0 rounded-sm transition-[height] duration-75"
            style={{
              height: bar.h,
              backgroundColor: bar.on ? "#A7E8C0" : "#7A746B",
            }}
          />
        ))}
      </span>
    </div>
  )
}

function NotePanel({
  noteDraft,
  onNoteDraftChange,
}: {
  noteDraft: string
  onNoteDraftChange: (value: string) => void
}) {
  const { t } = useLingui()
  return (
    <div className="flex flex-col gap-1.5 rounded-[11px] border border-[#3A352E] bg-[#252019] px-3 py-[11px]">
      <span className="text-[11px] font-semibold leading-[14px] text-[#9A938A]">
        <Trans>Quick note</Trans>
      </span>
      <textarea
        value={noteDraft}
        onChange={(event) => onNoteDraftChange(event.target.value)}
        aria-label={t`Quick note`}
        placeholder={t`Jot something for the summary…`}
        rows={2}
        autoFocus
        className="resize-none bg-transparent text-[13px] leading-[18px] text-[#FBF7F0] placeholder:text-[#6E685F] focus:outline-none"
      />
      <span className="text-[11px] leading-[14px] text-[#6E685F]">
        <Trans>Feeds the AI summary when the call ends.</Trans>
      </span>
    </div>
  )
}

function MuteButton({
  isMuted,
  onToggleMute,
}: {
  isMuted: boolean
  onToggleMute: () => void
}) {
  const { t } = useLingui()
  return (
    <ControlButton
      isToggle
      variant={isMuted ? "cream" : "idle"}
      label={isMuted ? t`Unmute` : t`Mute`}
      active={isMuted}
      onClick={onToggleMute}
    >
      {isMuted ? <MicLineOffIcon /> : <MicLineIcon />}
    </ControlButton>
  )
}

function KeypadButton({
  swap,
  onToggle,
}: {
  swap: "none" | "note" | "keypad"
  onToggle: () => void
}) {
  const { t } = useLingui()
  return (
    <ControlButton
      isToggle
      variant={swap === "keypad" ? "cream" : "idle"}
      label={t`Keypad`}
      ariaLabel={swap === "keypad" ? t`Hide dial pad` : t`Show dial pad`}
      active={swap === "keypad"}
      onClick={onToggle}
    >
      <DotGridIcon />
    </ControlButton>
  )
}

function NoteButton({
  swap,
  onToggle,
}: {
  swap: "none" | "note" | "keypad"
  onToggle: () => void
}) {
  const { t } = useLingui()
  return (
    <ControlButton
      isToggle
      variant={swap === "note" ? "brand" : "idle"}
      label={t`Note`}
      ariaLabel={swap === "note" ? t`Hide note` : t`Add a note`}
      active={swap === "note"}
      onClick={onToggle}
    >
      <NoteDocIcon />
    </ControlButton>
  )
}

function HangupButton({ onHangup }: { onHangup: () => void }) {
  const { t } = useLingui()
  return (
    <ControlButton
      variant="danger"
      label={t`End`}
      ariaLabel={t`Hang up`}
      onClick={onHangup}
    >
      <PhoneDownSolidIcon width={18} height={18} />
    </ControlButton>
  )
}

interface ControlButtonProps {
  variant: "idle" | "cream" | "brand" | "danger"
  label: string
  // Optional fixed accessible name; defaults to `label` (or
  // `activeLabel` while `active`).
  ariaLabel?: string
  // Alternate visible label + accessible name when active
  // (e.g. Mute → Unmute).
  activeLabel?: string
  active?: boolean
  // Whether this is a toggle (Mute/Keypad/Note). Only toggles
  // expose `aria-pressed`; a one-shot action like Hang up must
  // not report a pressed state to assistive tech.
  isToggle?: boolean
  onClick: () => void
  children: ReactNode
}

const BUTTON_BG: Record<ControlButtonProps["variant"], string> = {
  idle: "bg-[#2F2A24] text-[#E7E2D9] hover:bg-[#3A352E]",
  cream: "bg-[#F4EFE6] text-[#1E1B17] hover:bg-[#FBF7F0]",
  brand: "bg-[#E0A56A] text-[#1E1B17] hover:bg-[#E8B884]",
  danger: "bg-[#E5484D] text-white hover:bg-[#D63B40]",
}

function ControlButton({
  variant,
  label,
  ariaLabel,
  activeLabel,
  active = false,
  isToggle = false,
  onClick,
  children,
}: ControlButtonProps) {
  const visibleLabel = active && activeLabel ? activeLabel : label
  const accessibleName = ariaLabel ?? visibleLabel
  // The label color tracks the active variant so it reads as
  // "on": cream/brand controls get a bright label, idle ones
  // stay muted, End stays in its danger tint.
  const labelColor =
    variant === "cream"
      ? "text-[#FBF7F0] font-semibold"
      : variant === "brand"
        ? "text-[#E0A56A] font-semibold"
        : variant === "danger"
          ? "text-[#F0B3B5]"
          : "text-[#9A938A]"

  return (
    <div className="flex flex-1 flex-col items-center gap-[5px]">
      <button
        type="button"
        onClick={onClick}
        aria-pressed={isToggle ? active : undefined}
        aria-label={accessibleName}
        className={`flex size-11 shrink-0 items-center justify-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E0A56A]/60 ${BUTTON_BG[variant]}`}
      >
        {children}
      </button>
      <span className={`text-[11px] leading-[14px] ${labelColor}`}>
        {visibleLabel}
      </span>
    </div>
  )
}
